import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, Link } from "react-router-dom";
import { Notice } from "@/components/Notice";
import { auth, type SessionUser } from "@/features/auth/api/auth.api";
import { ApiError, UNAUTHORIZED_EVENT } from "@/lib/apiClient";
import { getToken, getRefreshToken } from "@/lib/authToken";
import { APP_PATH } from "@/lib/basePath";
import { clearReturnTo, saveReturnTo } from "@/lib/returnTo";

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  signIn: (identifier: string, password: string) => Promise<SessionUser>;
  signOut: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
  hasPermission: (key: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const userRef = useRef<SessionUser | null>(null);
  userRef.current = user;

  useEffect(() => {
    if (!getToken() && !getRefreshToken()) {
      setLoading(false);
      return;
    }
    auth.me()
      .then(setUser)
      .catch((e) => {
        if (!(e instanceof ApiError) || e.status !== 401) {
          console.error("me() failed", e);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      const prev = userRef.current;
      if (prev) {
        const { pathname, search, hash } = window.location;
        const routePath = pathname.startsWith(APP_PATH)
          ? pathname.slice(APP_PATH.length - 1)
          : pathname;
        saveReturnTo(`${routePath}${search}${hash}`, prev.userId);
      }
      setUser(null);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const value: AuthState = {
    user,
    loading,
    async signIn(identifier, password) {
      const u = await auth.login(identifier, password);
      setUser(u);
      return u;
    },
    async signOut() {
      clearReturnTo();
      await auth.logout();
      setUser(null);
    },
    async impersonate(userId) {
      setUser(await auth.impersonate(userId));
    },
    async stopImpersonating() {
      setUser(await auth.stopImpersonating());
    },
    hasPermission(key) {
      if (!user) return false;
      if (user.role === "ADMIN") return true;
      return user.permissions.includes(key);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function RequirePermission({
  perm,
  children,
}: {
  perm: string | string[];
  children: ReactNode;
}) {
  const { hasPermission } = useAuth();
  const keys = Array.isArray(perm) ? perm : [perm];
  if (keys.some((k) => hasPermission(k))) return <>{children}</>;

  return (
    <Notice tone="warn" className="mx-auto max-w-xl">
      <div className="space-y-2">
        <p className="font-medium">You don't have access to this page.</p>
        <p className="text-sm">
          Ask an administrator to grant the required permission, or head back to your{" "}
          <Link to="/" className="font-medium underline">
            dashboard
          </Link>
          .
        </p>
      </div>
    </Notice>
  );
}
